<template>
    <div id="form" class="steps" :class="{ 'relative bg-white lg:bg-brand-blue min-h-screen': useSpaLayout }">
        <div :class="{ 'hidden lg:block absolute bg-white w-1/2 right-0 inset-y-0': useSpaLayout }"></div>
        <div :class="{ 'flex flex-col lg:flex-row max-w-screen-xl mx-auto px-0 lg:px-5 min-h-screen': useSpaLayout }">
            <div class="w-full lg:w-2/5 flex flex-col justify-center items-start bg-brand-blue px-5 lg:px-0" v-show="useSpaLayout">
                <img class="hidden lg:block h-16" src="/assets/img/logo-nav.png" alt="" />
                <p class="mt-0 lg:mt-10 pr-0 lg:pr-28 py-8 lg:py-0 text-2xl lg:text-4xl font-semibold font-sans text-white">Join the <span class="text-amber-400">1,000,000+</span> people who have saved up to 80% on legal fees</p>
            </div>

            <div :class="layoutClasses">
                <progress-bar class="px-5 lg:px-0" v-show="useSpaLayout"></progress-bar>
                <template v-if="ready">
                    <component :is="stepComponent" v-bind="stepProps"></component>
                </template>
                <template v-else>
                    <slot></slot>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { findKey, get } from 'lodash';
import { mapGetters } from 'vuex';

import Form from '../form.js';

import LeadStore from './store/lead.js';
import MetaStore from './store/meta.js';
import ProgressBar from './LeadCapture/ProgressBar.vue';
import RegionsModal from './LeadCapture/RegionsModal.vue';
import StepLocation from './LeadCapture/StepLocation.vue';
import StepIssue from './LeadCapture/StepIssue.vue';
import StepQualify from './LeadCapture/StepQualify.vue';
import StepContact from "./LeadCapture/StepContact.vue";
import Conversion from "./LeadCapture/Conversion.vue";

export default {
    components: {
        ProgressBar,
        RegionsModal,
        StepLocation,
        StepIssue,
        StepQualify,
        StepContact,
        Conversion,
    },

    props: {
        errors: null,

        formData: {
            type: Object,
            required: true
        },

        dynamic: {
            type: Object,
            default: () => {}
        },

        preHeadline: {
            type: String,
            default: 'Find an'
        },

        heroBullets: {
            type: Array,
            default: []
        },

        logoSrc: {
            type: String,
            required: true
        },

        previousRequestText: {
            type: Object,
            default() {
                return {};
            }
        },

        redirectUrl: {
            type: String,
            default: ''
        },

        pageLocation: {
            type: String,
            default() {
                return '';
            }
        },

        validation: {
            type: Object,
            default() {
                return {};
            }
        },
    },

    data: () => ({
        ready: false,
        step: null,
        stepComponents: {
            1: 'step-location',
            2: 'step-issue',
            3: 'step-qualify',
            4: 'step-contact',
            5: 'conversion'
        },
    }),

    created() {
        this.createLeadStore();
        this.createMetaStore();

        this.initializeEventListeners();
        this.initializeFormData();
        this.initializeExtraData();
        this.loadLocalStorageData();
    },

    mounted () {
        this.determineAppropriateStep();
        this.markReady();
        this.updateNavbarVisibility();

        if (this.redirectUrl) {
            this.$store.commit('lead/redirectUrl', this.redirectUrl);
        }

        this.$store.commit('lead/pageLocation', this.pageLocation);
    },

    computed: {
        ...mapGetters('lead', [
            'city',
            'disambiguate',
            'form',
            'locationId',
            'regions',
            'state',
            'stepLocationIsIncomplete',
            'stepIssueIsIncomplete'
        ]),

        /**
         * @returns {string}
         */
        stepComponent() {
            return get(this.stepComponents, this.step);
        },

        /**
         * @returns {object}
         */
        stepProps() {
            if (this.stepComponent === 'step-location') {
                return {
                    previousRequestText: this.previousRequestText
                };
            }

            if (this.stepComponent === 'step-qualify') {
                return {
                    dynamic: this.dynamic
                };
            }

            if (this.stepComponent === 'step-issue') {
                return {
                    validation: this.validation
                };
            }

            return null;
        },

        useSpaLayout() {
            return this.step >= 2 && this.step <= 4;
        },

        layoutClasses() {
            if (this.useSpaLayout) {
                return 'w-full lg:w-3/5 bg-white'
            }

            return 'mx-auto';
        },
    },

    methods: {
        /**
         * Dynamically register the lead storage module.
         */
        createLeadStore() {
            this.$store.registerModule('lead', LeadStore);
        },

        /**
         * Dynamically register the meta storage module.
         */
        createMetaStore() {
            this.$store.registerModule('meta', MetaStore);
        },


        /**
         * Determine the proper step to display based on local storage data and url query.
         */
        determineAppropriateStep() {
            return this.specificStep(Math.min(this.intendedStep(), this.incompleteStep()));
        },

        /**
         * Determine which step the user hasn't completed.
         *
         * @returns {number}
         */
        incompleteStep() {
            const steps = {
                1: this.stepLocationIsIncomplete,
                2: this.stepIssueIsIncomplete,
                3: false,
                4: true
            };

            const key = findKey(steps, (step) => {
                return step;
            });

            return Number(key);
        },

        /**
         * Get the intended step from the url query.
         *
         * @returns {number}
         */
        intendedStep() {
            let url = new URL(window.location.href);

            return Number(url.searchParams.get('step')) || 1;
        },

        /**
         * Go back on step in the form.
         */
        decrementStep() {
            if (this.step > 1) {
                this.step--;

                if (this.step === 3 && !this.form.has_qualifying_question) {
                    this.step--;
                }
                this.pushHistoryState();
                this.dispatchGlobalEvent('stepDecremented');
            }
        },

        /**
         * Fire global event for A/B testing.
         *
         * @param {string} event
         */
        dispatchGlobalEvent(event) {
            const globalEvent = new CustomEvent('stepIncremented', {
                detail: { step: this.step}
            });

            document.dispatchEvent(globalEvent);
        },

        /**
         * Go to the next step in the form.
         */
        incrementStep() {
            if (this.step < Object.keys(this.stepComponents).length) {
                this.step++;
                this.pushHistoryState();
                this.dispatchGlobalEvent('stepIncremented');
            }
        },

        /**
         * Load data stored in local storage, if any, and put it in vuex store.
         */
        loadLocalStorageData() {
            let form = window.localStorage.getItem('lcsForm');

            if (form) {
                form = JSON.parse(form);

                // If the lead already has a redirect link, redirect them now.
                this.redirectIfNecessary(form);

                this.$store.commit('lead/updateForm', form);

                this.$store.commit('lead/city', get(form, 'city'));
                this.$store.commit('lead/locationId', get(form, 'locationId'));
                this.$store.commit('lead/state', get(form, 'state'));
            }
        },

        /**
         * Mark the lead capture form as ready to display it to the user.
         */
        markReady() {
            this.ready = true;
        },

        /**
         * Check if a redirect url already exists and send the lead there.
         *
         * @param {object} form
         */
        redirectIfNecessary(form) {
            const redirect = get(form, 'redirectUrl');

            if (redirect) {
                window.location.href = redirect;
            }
        },

        /**
         * Set a specific step to display.
         *
         * @param {number} step
         */
        specificStep(step) {
            this.step = step;

            if (this.ready) {
                this.pushHistoryState();
            } else {
                this.markReady();
            }

            this.dispatchGlobalEvent('specificStep');
        },

        /**
         * Watch for specific events.
         */
        initializeEventListeners() {
            this.$event.$on('step:next', this.incrementStep);
            this.$event.$on('step:previous', this.decrementStep);
            this.$event.$on('step:specific', this.specificStep);

            window.onpopstate = () => {
                let url = new URL(window.location.href);
                this.step = url.searchParams.get('step') || 1;
            };
        },

        /**
         * Initialize extra data in the vuex store.
         */
        initializeExtraData() {
            this.$store.commit('lead/dynamic', this.dynamic);
            this.$store.commit('lead/heroBullets', this.heroBullets);
            this.$store.commit('lead/preHeadline', this.preHeadline);
            this.$store.commit('lead/logoSrc', this.logoSrc);
        },

        /**
         * Initialize the form object in the vuex store with available data.
         */
        async initializeFormData() {
            this.$store.commit('lead/initializeForm', new Form({
                city: this.city,
                postcode: this.zipcode,
                region_id: this.formData.region,
                issue: this.formData.issue,
                overridden_division_slug: this.formData.overridden_division_slug,
                overriding_question: this.formData.overriding_question,
                first_name: this.formData.first_name,
                last_name: this.formData.last_name,
                phone: this.formData.phone,
                phone_alt: this.formData.phone_alt,
                email: this.formData.email,
                sms_opted_in: this.formData.sms_opted_in,
                ga_client_id: window.SHARED.gaClientId,
                ga_session_id: window.SHARED.gaSessionId,
                has_qualifying_question: false
            }, this.errors));
        },

        /**
         * After step has been updated, change the browser history by adding a new state.
         */
        pushHistoryState() {
            let url = new URL(window.location.href);
            let currentStep = url.searchParams.get('step');

            if (currentStep != this.step && this.step < Object.keys(this.stepComponents).length) {
                url.searchParams.set('step', this.step);

                window.history.pushState({}, document.title, url)
            }
        },

        updateNavbarVisibility() {
            if (this.useSpaLayout) {
                this.$store.commit('showSpaLayout', true);
            } else {
                this.$store.commit('showSpaLayout', false);
            }
        }
    },

    watch: {
        step() {
            this.$event.$emit('step:on-step', this.step);
            this.dispatchGlobalEvent('onStep');
            this.updateNavbarVisibility();
        }
    },
}
</script>

<style scoped>
.steps >>> .is-valid {
    @apply pr-9 border-green-500 bg-[length:1.2rem] bg-no-repeat bg-[right_.5rem_center];
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
}
</style>
